import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";

import useIdentity from "@/features/ui/hooks/use-identity";
import { isBuyer } from "@/features/ui/helpers/identity-helpers";
import useGetMySelf from "@/features/auth/use-get-my-self";
import axiosClient from "@/utils/axios-client";

type Phase = "loading" | "selecting" | "linking" | "done" | "error";

const PATHPICK_HOME = "/pathpick/search-products";

/**
 * 네이버 커머스솔루션 사용연결 온보딩.
 *
 * connect 엔드포인트가 검증 후 이 페이지로 `?link_token=...` 를 달고 리다이렉트한다.
 * PrivateLayout 아래 라우트이므로 미로그인 사용자는 자동으로 로그인(→ redirect_url 복귀)된다.
 * 바이어를 1개만 보유하면 자동으로, 여러 개면 선택 UI 로 고른 뒤 accountUid ↔ buyer_id 를 연결한다.
 */
export default function NaverSolutionOnboardingPage() {
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get("link_token");
  const identity = useIdentity();
  const { data: user, isLoading } = useGetMySelf();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>("loading");
  const [channelName, setChannelName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [selectedBuyerId, setSelectedBuyerId] = useState<number | undefined>();
  const linkedRef = useRef(false);

  const doLink = useCallback(
    async (buyerId: number) => {
      if (linkedRef.current || !linkToken) return;
      linkedRef.current = true;
      setPhase("linking");
      try {
        const { data: pending } = await axiosClient.get(
          "/pathpick/naver_solution/pending",
          { params: { link_token: linkToken } }
        );
        setChannelName(pending?.channel_name ?? null);

        // 이미 이 바이어에 연결돼 있으면 link 재호출 없이 완료 처리.
        const alreadyLinkedToMe =
          pending?.already_linked && pending?.linked_buyer_id === buyerId;
        if (!alreadyLinkedToMe) {
          await axiosClient.post(
            `/pathpick/naver_solution/buyers/${buyerId}/link`,
            { link_token: linkToken }
          );
        }
        setPhase("done");
      } catch (e) {
        linkedRef.current = false; // 재시도 가능하도록 되돌린다.
        setErrorMessage(
          "연동에 실패했어요. 링크가 만료되었을 수 있으니 스마트스토어에서 솔루션 [사용하기]로 다시 진입해 주세요."
        );
        setPhase("error");
      }
    },
    [linkToken]
  );

  // 데이터 로드 후 분기: 바이어 0 / 1 / 다수
  useEffect(() => {
    if (phase !== "loading") return;

    if (!linkToken) {
      setErrorMessage(
        "잘못된 접근입니다. 스마트스토어에서 솔루션 [사용하기]로 다시 진입해 주세요."
      );
      setPhase("error");
      return;
    }
    if (isLoading || !user) return; // 유저 정보 로딩 대기

    const buyerCompanies = user.companies.filter((c) => c.type === "BUYER");
    if (buyerCompanies.length === 0) {
      setErrorMessage("바이어 계정으로 로그인한 뒤 다시 시도해 주세요.");
      setPhase("error");
      return;
    }
    if (buyerCompanies.length === 1) {
      doLink(buyerCompanies[0].id);
      return;
    }
    // 다수 바이어 → 선택 UI. 활성 회사가 바이어면 그걸 기본값으로.
    const preferred = isBuyer(identity)
      ? identity?.company?.id
      : buyerCompanies[0].id;
    setSelectedBuyerId(preferred);
    setPhase("selecting");
  }, [phase, isLoading, user, linkToken, identity, doLink]);

  const buyerCompanies = (user?.companies ?? []).filter(
    (c) => c.type === "BUYER"
  );

  return (
    <Box padding="24px" height="100%" overflow="auto" bg="#FCFCFE">
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minH="500px"
        gap="16px"
        textAlign="center"
      >
        {(phase === "loading" || phase === "linking") && (
          <>
            <Spinner size="lg" color="#1272EF" />
            <Text fontSize="16px" color="#8F8F8C">
              {phase === "linking"
                ? "스마트스토어 연동을 처리하고 있어요…"
                : "불러오는 중…"}
            </Text>
          </>
        )}

        {phase === "selecting" && (
          <>
            <Heading fontSize="24px" fontWeight="700" color="#303136">
              연동할 바이어 선택
            </Heading>
            <Text fontSize="15px" color="#8F8F8C">
              {channelName
                ? `‘${channelName}’ 스마트스토어를 연결할 바이어를 선택해 주세요.`
                : "이 스마트스토어를 연결할 패스레이드 바이어를 선택해 주세요."}
            </Text>
            <Select
              value={selectedBuyerId ?? ""}
              onChange={(e) => setSelectedBuyerId(Number(e.target.value))}
              width="280px"
              bg="white"
            >
              {buyerCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.orgName}
                </option>
              ))}
            </Select>
            <Button
              mt="8px"
              bg="#1272EF"
              color="white"
              _hover={{ bg: "#0F5FCF" }}
              isDisabled={!selectedBuyerId}
              onClick={() => selectedBuyerId && doLink(selectedBuyerId)}
            >
              연동하기
            </Button>
          </>
        )}

        {phase === "done" && (
          <>
            <Heading fontSize="28px" fontWeight="700" color="#303136">
              연동 완료
            </Heading>
            <Text fontSize="16px" color="#8F8F8C">
              {channelName
                ? `‘${channelName}’ 스마트스토어가 패스레이드에 연동되었어요.`
                : "스마트스토어가 패스레이드에 연동되었어요."}
            </Text>
            <Button
              mt="8px"
              bg="#1272EF"
              color="white"
              _hover={{ bg: "#0F5FCF" }}
              onClick={() => navigate(PATHPICK_HOME)}
            >
              패스픽으로 이동
            </Button>
          </>
        )}

        {phase === "error" && (
          <>
            <Heading fontSize="28px" fontWeight="700" color="#303136">
              연동을 완료하지 못했어요
            </Heading>
            <Text fontSize="16px" color="#8F8F8C" maxW="420px">
              {errorMessage}
            </Text>
            <Button
              mt="8px"
              variant="outline"
              borderColor="#D9D9D9"
              color="#303136"
              onClick={() => navigate(PATHPICK_HOME)}
            >
              패스픽으로 이동
            </Button>
          </>
        )}
      </Flex>
    </Box>
  );
}
